/** XeAgents.ai**/
import { useState, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import { Search, Download, Loader2, Phone, Calendar, Clock, MessageSquare, RefreshCw, Eye, Play, Pause, Volume2, PhoneIncoming, PhoneOutgoing, CheckCircle2, XCircle, Mic, FileText, Sparkles, Target, Layers, ChevronDown } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { formatDate } from "@/lib/date-format";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";

interface Call {
  id: string;
  contactId: string | null;
  campaignId: string | null;
  phoneNumber?: string | null;
  status: string;
  duration: number | null;
  classification: string | null;
  sentiment: string | null;
  recordingUrl: string | null;
  transcript: string | null;
  aiSummary: string | null;
  metadata: Record<string, any> | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  callDirection: string | null;
  elevenLabsConversationId?: string | null;
  campaign?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName?: string; phone: string } | null;
  incomingConnection?: { id: string; agentId: string } | null;
}

// Calls that belong to no campaign (inbound, one-off dials) get their own bucket
const NO_CAMPAIGN = "none";

// How many calls a campaign section shows before "show all"
const GROUP_PREVIEW_SIZE = 5;

// How many campaigns fit on one page before paging
const CAMPAIGNS_PER_PAGE = 10;

type CallPagination = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  paginatedItems: Call[];
  handlePageChange: (page: number) => void;
  handleItemsPerPageChange: (newItemsPerPage: number) => void;
};

export default function Calls() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sentimentFilter, setSentimentFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [leadFilter, setLeadFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [groupByCampaign, setGroupByCampaign] = useState(true);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignsPerPage, setCampaignsPerPage] = useState(CAMPAIGNS_PER_PAGE);
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);
  const [loadingRecording, setLoadingRecording] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // The sidebar's Transcript and Recordings items link to ?tab=..., so the
  // active tab has to come from the URL rather than from local state
  const searchString = useSearch();
  const tabParam = new URLSearchParams(searchString).get("tab");
  const activeTab = tabParam === "transcribed" || tabParam === "recordings" ? tabParam : "all";

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(searchString);
    if (value === "all") {
      params.delete("tab");
    } else {
      params.set("tab", value);
    }
    const query = params.toString();
    setLocation(query ? `/app/calls?${query}` : "/app/calls", { replace: true });
  };

  const { data: calls, isLoading } = useQuery<Call[]>({
    queryKey: ["/api/calls"],
  });

  const syncRecordingsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/calls/sync-recordings");
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calls"] });
      
      if (data.total === 0) {
        toast({
          title: "No calls to sync",
          description: "All completed calls have been synced.",
        });
      } else if (data.success > 0) {
        toast({
          title: "Sync Complete",
          description: `Synced ${data.success} of ${data.total} calls. ${data.failed > 0 ? `${data.failed} could not be synced.` : ''}`,
        });
      } else {
        toast({
          title: "No new data found",
          description: "Recordings may still be processing. Try again in a few minutes.",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Sync failed",
        description: error.message || "Failed to sync recordings. Please try again.",
        variant: "destructive",
      });
    },
  });

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">{t('calls.status.completed')}</Badge>;
      case "failed":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">{t('calls.status.failed')}</Badge>;
      case "in_progress":
      case "in-progress":
        return <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">{t('calls.status.inProgress')}</Badge>;
      case "ended":
        return <Badge className="bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20">{t('calls.status.ended')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSentimentBadge = (sentiment: string | null) => {
    if (!sentiment) return null;
    switch (sentiment) {
      case "positive":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">{t('calls.sentiment.positive')}</Badge>;
      case "negative":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">{t('calls.sentiment.negative')}</Badge>;
      case "neutral":
        return <Badge className="bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20">{t('calls.sentiment.neutral')}</Badge>;
      default:
        return <Badge variant="outline">{sentiment}</Badge>;
    }
  };

  const getClassificationBadge = (classification: string | null) => {
    if (!classification) return null;
    switch (classification) {
      case "hot":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">{t('calls.classification.hot')}</Badge>;
      case "warm":
        return <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">{t('calls.classification.warm')}</Badge>;
      case "cold":
        return <Badge className="bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20">{t('calls.classification.cold')}</Badge>;
      case "lost":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">{t('calls.classification.lost')}</Badge>;
      case "completed_successful":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">{t('calls.classification.successful')}</Badge>;
      case "completed_failed":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">{t('calls.status.failed')}</Badge>;
      case "completed":
        return <Badge className="bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20">{t('calls.status.completed')}</Badge>;
      default:
        return <Badge variant="outline">{classification}</Badge>;
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (!isValid(date)) return null;
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const formatCallDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Unknown date";
    const date = new Date(dateString);
    if (!isValid(date)) return "Unknown date";
    return format(date, "MMM d, h:mm a");
  };

  const hasRecording = (call: Call) => {
    return call.recordingUrl || call.elevenLabsConversationId;
  };

  const handlePlayRecording = async (e: React.MouseEvent, call: Call) => {
    e.stopPropagation();
    
    if (playingCallId === call.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingCallId(null);
      return;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setLoadingRecording(call.id);
    
    try {
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      
      const response = await fetch(`/api/calls/${call.id}/recording`, {
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch recording");
      }
      
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      setPlayingCallId(call.id);
      setLoadingRecording(null);
      
      audio.onended = () => {
        setPlayingCallId(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        toast({
          title: "Playback Error",
          description: "Unable to play this recording.",
          variant: "destructive",
        });
        setPlayingCallId(null);
        audioRef.current = null;
      };
      
      await audio.play();
    } catch (error: any) {
      setLoadingRecording(null);
      toast({
        title: "Recording Unavailable",
        description: error.message || "Could not load recording. Try syncing recordings first.",
        variant: "destructive",
      });
      setPlayingCallId(null);
    }
  };

  const getDirectionIcon = (direction: string | null) => {
    if (direction === 'incoming') {
      return <PhoneIncoming className="h-4 w-4 text-emerald-500" />;
    }
    return <PhoneOutgoing className="h-4 w-4 text-blue-500" />;
  };

  const handleExport = () => {
    const data = filteredCalls.length > 0 ? filteredCalls : (calls || []);
    if (data.length === 0) {
      toast({ title: "No calls to export", description: "There are no calls to export." });
      return;
    }
    const headers = ["ID", "Phone Number", "Contact Name", "Campaign", "Direction", "Status", "Sentiment", "Classification", "Duration (s)", "Started At", "Ended At", "AI Summary"];
    const rows = data.map(call => [
      call.id,
      call.phoneNumber || call.contact?.phone || "",
      call.contact ? `${call.contact.firstName} ${call.contact.lastName || ""}`.trim() : "",
      call.campaign?.name || "",
      call.callDirection || "",
      call.status,
      call.sentiment || "",
      call.classification || "",
      call.duration ?? "",
      call.startedAt ? formatDate(call.startedAt, "yyyy-MM-dd HH:mm:ss") : "",
      call.endedAt ? formatDate(call.endedAt, "yyyy-MM-dd HH:mm:ss") : "",
      (call.aiSummary || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `calls-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const callTimestamp = (call: Call) => {
    const value = call.startedAt || call.createdAt;
    const time = value ? new Date(value).getTime() : 0;
    return isNaN(time) ? 0 : time;
  };

  const campaignKeyOf = (call: Call) => call.campaign?.id || call.campaignId || NO_CAMPAIGN;

  const campaignNameOf = (call: Call) => {
    if (call.campaign?.name) return call.campaign.name;
    const id = call.campaign?.id || call.campaignId;
    return id ? `Campaign ${id.slice(0, 8)}` : t('calls.filters.noCampaign');
  };

  // Every campaign that actually has calls, busiest first, so the filter and
  // the grouped view always agree on what exists
  const campaignOptions = useMemo(() => {
    const seen = new Map<string, { key: string; name: string; count: number; lastRunAt: number }>();
    for (const call of calls || []) {
      const key = campaignKeyOf(call);
      const ranAt = callTimestamp(call);
      const existing = seen.get(key);
      if (existing) {
        existing.count += 1;
        if (ranAt > existing.lastRunAt) existing.lastRunAt = ranAt;
      } else {
        seen.set(key, { key, name: campaignNameOf(call), count: 1, lastRunAt: ranAt });
      }
    }
    return Array.from(seen.values()).sort((a, b) => b.lastRunAt - a.lastRunAt);
  }, [calls]);

  const groupCallsByCampaign = (list: Call[]) => {
    const groups = new Map<string, { key: string; name: string; calls: Call[]; lastRunAt: number }>();
    for (const call of list) {
      const key = campaignKeyOf(call);
      const ranAt = callTimestamp(call);
      const existing = groups.get(key);
      if (existing) {
        existing.calls.push(call);
        if (ranAt > existing.lastRunAt) existing.lastRunAt = ranAt;
      } else {
        groups.set(key, { key, name: campaignNameOf(call), calls: [call], lastRunAt: ranAt });
      }
    }
    const ordered = Array.from(groups.values());
    for (const group of ordered) {
      group.calls.sort((a, b) => callTimestamp(b) - callTimestamp(a));
    }
    return ordered.sort((a, b) => b.lastRunAt - a.lastRunAt);
  };

  const filteredCalls = (calls || []).filter((call) => {
    const contactFullName = call.contact ? `${call.contact.firstName} ${call.contact.lastName || ""}`.toLowerCase() : "";
    const matchesSearch = 
      searchQuery === "" || 
      contactFullName.includes(searchQuery.toLowerCase()) ||
      call.contact?.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.transcript?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.aiSummary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || call.status === statusFilter;
    const matchesSentiment = sentimentFilter === "all" || call.sentiment === sentimentFilter;
    const matchesDirection = directionFilter === "all" || call.callDirection === directionFilter;
    const matchesLead = leadFilter === "all" || call.classification === leadFilter;
    const matchesCampaign = campaignFilter === "all" || campaignKeyOf(call) === campaignFilter;
    
    return matchesSearch && matchesStatus && matchesSentiment && matchesDirection && matchesLead && matchesCampaign;
  });

  const callsWithTranscripts = filteredCalls.filter(call => call.transcript);
  const callsWithRecordings = filteredCalls.filter(call => hasRecording(call));

  const allPagination = usePagination(filteredCalls, 10);
  const transcribedPagination = usePagination(callsWithTranscripts, 10);
  const recordingsPagination = usePagination(callsWithRecordings, 10);

  const totalCalls = calls?.length || 0;
  const completedCalls = calls?.filter(c => c.status === 'completed').length || 0;
  const incomingCalls = calls?.filter(c => c.callDirection === 'incoming').length || 0;
  const outgoingCalls = calls?.filter(c => c.callDirection === 'outgoing').length || 0;
  const campaignCount = campaignOptions.filter(option => option.key !== NO_CAMPAIGN).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderCallCard = (call: Call, testIdPrefix: string = "", hideCampaign: boolean = false) => (
    <div 
      key={call.id}
      className="group bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={() => setLocation(`/app/calls/${call.id}`)}
      data-testid={`card-call-${testIdPrefix}${call.id}`}
    >
      <div className="flex">
        <div className={`w-1 ${call.status === 'completed' ? 'bg-emerald-500' : call.status === 'failed' ? 'bg-rose-500' : 'bg-amber-500'}`} />
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                call.callDirection === 'incoming' 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                  : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
              }`}>
                {getDirectionIcon(call.callDirection)}
              </div>
              
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-foreground truncate">
                    {call.contact && call.contact.firstName && call.contact.firstName.toLowerCase() !== 'unknown'
                      ? `${call.contact.firstName} ${call.contact.lastName || ""}`.trim() 
                      : (call.contact?.phone || call.phoneNumber || `Call ${call.id.slice(0, 8)}`)}
                  </h3>
                  {getStatusBadge(call.status)}
                  {getSentimentBadge(call.sentiment)}
                  {getClassificationBadge(call.classification)}
                </div>
                
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  {call.contact?.phone && (
                    <span className="font-mono text-xs">{call.contact.phone}</span>
                  )}
                  {!call.contact?.phone && call.phoneNumber && (
                    <span className="font-mono text-xs">{call.phoneNumber}</span>
                  )}
                  {call.campaign && !hideCampaign && (
                    <span className="inline-flex items-center gap-1 min-w-0 max-w-[180px]">
                      <Target className="h-3 w-3 text-orange-500 flex-shrink-0" />
                      <span className="truncate">{call.campaign.name}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasRecording(call) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={(e) => handlePlayRecording(e, call)}
                  disabled={loadingRecording === call.id}
                  data-testid={`button-play-${testIdPrefix}${call.id}`}
                >
                  {loadingRecording === call.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : playingCallId === call.id ? (
                    <Pause className="h-4 w-4 text-primary" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-medium">{formatDuration(call.duration)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatRelativeTime(call.createdAt) || formatCallDate(call.createdAt)}</span>
            </div>
          </div>
          
          {(call.aiSummary || call.transcript) && (
            <div className="mt-3 pt-3 border-t border-border/50">
              {call.aiSummary && (
                <div className="flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {call.aiSummary}
                  </p>
                </div>
              )}
              {!call.aiSummary && call.transcript && (
                <div className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {call.transcript}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between gap-2 mt-3">
            <div className="flex items-center gap-2">
              {hasRecording(call) && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Mic className="h-3 w-3" />
                  <span>{t('calls.details.recording')}</span>
                </div>
              )}
              {call.transcript && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3" />
                  <span>{t('calls.details.transcript')}</span>
                </div>
              )}
              {call.aiSummary && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Sparkles className="h-3 w-3" />
                  <span>{t('calls.details.aiAnalysis')}</span>
                </div>
              )}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setLocation(`/app/calls/${call.id}`);
              }}
              data-testid={`button-view-details-${testIdPrefix}${call.id}`}
            >
              <Eye className="h-4 w-4 mr-1" />
              {t('calls.viewDetails')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Campaign-wise view: one section per campaign with its own headline
  // numbers, so a campaign can be judged without opening every call
  const renderCampaignGroups = (list: Call[], testIdPrefix: string) => {
    const groups = groupCallsByCampaign(list);

    // One page of campaigns at a time; the page can shrink under a filter
    const totalCampaignPages = Math.max(1, Math.ceil(groups.length / campaignsPerPage));
    const currentCampaignPage = Math.min(campaignPage, totalCampaignPages);
    const pageStart = (currentCampaignPage - 1) * campaignsPerPage;
    const visibleGroups = groups.slice(pageStart, pageStart + campaignsPerPage);

    return (
      <div className="space-y-4">
        {visibleGroups.map((group) => {
          const groupId = `${testIdPrefix}${group.key}`;
          // Closed until asked for, so opening the page costs one row per campaign.
          // Filtering down to a single campaign opens it, since that is the intent.
          const isOpen = openGroups[groupId] ?? campaignFilter !== "all";
          const isExpanded = expandedGroups[groupId];
          const lastRun = group.lastRunAt > 0 ? new Date(group.lastRunAt).toISOString() : null;
          const visibleCalls = isExpanded ? group.calls : group.calls.slice(0, GROUP_PREVIEW_SIZE);
          const completed = group.calls.filter(call => call.status === 'completed').length;
          const qualified = group.calls.filter(call => call.classification === 'hot' || call.classification === 'warm').length;
          const talkTime = group.calls.reduce((total, call) => total + (call.duration || 0), 0);

          return (
            <div
              key={groupId}
              className="border rounded-lg bg-muted/30 overflow-hidden"
              data-testid={`group-campaign-${groupId}`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors"
                onClick={() => setOpenGroups(prev => ({ ...prev, [groupId]: !(prev[groupId] ?? campaignFilter !== "all") }))}
                data-testid={`button-toggle-group-${groupId}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    group.key === NO_CAMPAIGN
                      ? 'bg-slate-500/10 text-slate-600 dark:text-slate-400'
                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                  }`}>
                    <Target className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground truncate">{group.name}</h3>
                      <Badge variant="secondary" className="flex-shrink-0">{group.calls.length}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {lastRun && (
                        <>
                          {t('calls.lastRun', { value: formatRelativeTime(lastRun) || formatCallDate(lastRun) })}
                          {' · '}
                        </>
                      )}
                      {t('calls.groupSummary.completed', { value: completed })}
                      {' · '}
                      {t('calls.groupSummary.qualified', { value: qualified })}
                      {' · '}
                      {t('calls.groupSummary.talkTime', { value: formatDuration(talkTime) })}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-3">
                  <div className="grid gap-3">
                    {visibleCalls.map((call) => renderCallCard(call, `${testIdPrefix}${group.key}-`, true))}
                  </div>
                  {group.calls.length > GROUP_PREVIEW_SIZE && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }))}
                      data-testid={`button-expand-group-${groupId}`}
                    >
                      {isExpanded
                        ? t('calls.showLess')
                        : t('calls.showAllInCampaign', { value: group.calls.length })}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <DataPagination
          currentPage={currentCampaignPage}
          totalPages={totalCampaignPages}
          totalItems={groups.length}
          itemsPerPage={campaignsPerPage}
          onPageChange={setCampaignPage}
          onItemsPerPageChange={(size) => {
            setCampaignsPerPage(size);
            setCampaignPage(1);
          }}
          data-testid={`pagination-campaigns-${testIdPrefix || "all"}`}
        />
      </div>
    );
  };

  const renderCallList = (list: Call[], pagination: CallPagination, testIdPrefix: string, listId: string) => {
    if (groupByCampaign) {
      return renderCampaignGroups(list, testIdPrefix);
    }

    return (
      <>
        <div className="grid gap-3">
          {pagination.paginatedItems.map((call) => renderCallCard(call, testIdPrefix))}
        </div>
        <DataPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={pagination.handlePageChange}
          onItemsPerPageChange={pagination.handleItemsPerPageChange}
          data-testid={`pagination-${listId}-calls`}
        />
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 via-indigo-50 to-sky-50 dark:from-blue-950/40 dark:via-indigo-950/30 dark:to-sky-950/40 border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t('calls.title')}</h1>
              <p className="text-muted-foreground text-sm">{t('calls.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => syncRecordingsMutation.mutate()}
              disabled={syncRecordingsMutation.isPending}
              className="bg-background/80"
              data-testid="button-sync-recordings"
            >
              {syncRecordingsMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t('calls.syncRecordings')}
            </Button>
            <Button
              variant="default"
              data-testid="button-export-calls"
              onClick={handleExport}
            >
              <Download className="h-4 w-4 mr-2" />
              {t('common.export')}
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-2xl font-bold text-foreground" data-testid="text-total-calls">{totalCalls}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t('calls.stats.totalCalls')}</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-2xl font-bold text-foreground" data-testid="text-total-campaigns">{campaignCount}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t('calls.stats.campaigns')}</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-2xl font-bold text-foreground">{completedCalls}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t('calls.status.completed')}</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <PhoneIncoming className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-2xl font-bold text-foreground">{incomingCalls}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t('calls.filters.incoming')}</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <PhoneOutgoing className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-2xl font-bold text-foreground">{outgoingCalls}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{t('calls.filters.outgoing')}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('calls.searchPlaceholder')}
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="input-search-calls"
          />
        </div>
        <Select value={campaignFilter} onValueChange={setCampaignFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="select-filter-campaign">
            <SelectValue placeholder={t('calls.filters.campaign')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calls.filters.allCampaigns')}</SelectItem>
            {campaignOptions.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                <span className="flex items-center gap-2">
                  <span className="truncate">
                    {option.key === NO_CAMPAIGN ? t('calls.filters.noCampaign') : option.name} ({option.count})
                  </span>
                  {option.lastRunAt > 0 && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatRelativeTime(new Date(option.lastRunAt).toISOString())}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-filter-status">
            <SelectValue placeholder={t('calls.details.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calls.filters.allStatus')}</SelectItem>
            <SelectItem value="completed">{t('calls.status.completed')}</SelectItem>
            <SelectItem value="failed">{t('calls.status.failed')}</SelectItem>
            <SelectItem value="in_progress">{t('calls.status.inProgress')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-filter-sentiment">
            <SelectValue placeholder={t('calls.details.sentiment')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calls.filters.allSentiment')}</SelectItem>
            <SelectItem value="positive">{t('calls.sentiment.positive')}</SelectItem>
            <SelectItem value="neutral">{t('calls.sentiment.neutral')}</SelectItem>
            <SelectItem value="negative">{t('calls.sentiment.negative')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={directionFilter} onValueChange={setDirectionFilter}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-filter-direction">
            <SelectValue placeholder={t('calls.details.direction')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calls.filters.allDirections')}</SelectItem>
            <SelectItem value="incoming">{t('calls.filters.incoming')}</SelectItem>
            <SelectItem value="outgoing">{t('calls.filters.outgoing')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={leadFilter} onValueChange={setLeadFilter}>
          <SelectTrigger className="w-full sm:w-[130px]" data-testid="select-filter-lead">
            <SelectValue placeholder={t('calls.filters.leadQuality')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('calls.filters.allLeads')}</SelectItem>
            <SelectItem value="hot">{t('calls.classification.hot')}</SelectItem>
            <SelectItem value="warm">{t('calls.classification.warm')}</SelectItem>
            <SelectItem value="cold">{t('calls.classification.cold')}</SelectItem>
            <SelectItem value="lost">{t('calls.classification.lost')}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={groupByCampaign ? "default" : "outline"}
          onClick={() => setGroupByCampaign(prev => !prev)}
          className="w-full sm:w-auto"
          data-testid="button-toggle-group-by-campaign"
        >
          <Layers className="h-4 w-4 mr-2" />
          {groupByCampaign ? t('calls.groupedByCampaign') : t('calls.newestFirst')}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">
            {t('calls.allCalls')} ({filteredCalls.length})
          </TabsTrigger>
          <TabsTrigger value="transcribed" data-testid="tab-transcribed">
            {t('calls.transcribed')} ({callsWithTranscripts.length})
          </TabsTrigger>
          <TabsTrigger value="recordings" data-testid="tab-recordings">
            {t('calls.recordings')} ({callsWithRecordings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3">
          {filteredCalls.length === 0 ? (
            <Card className="p-12 text-center">
              <Phone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">{t('calls.noCalls')}</h3>
              <p className="text-muted-foreground text-sm">
                {calls?.length === 0
                  ? t('calls.createCampaignToStart')
                  : t('calls.noMatchingFilters')}
              </p>
            </Card>
          ) : (
            renderCallList(filteredCalls, allPagination, "", "all")
          )}
        </TabsContent>

        <TabsContent value="transcribed" className="space-y-3">
          {callsWithTranscripts.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">{t('calls.noTranscribed')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('calls.transcriptsWillAppear')}
              </p>
            </Card>
          ) : (
            renderCallList(callsWithTranscripts, transcribedPagination, "transcribed-", "transcribed")
          )}
        </TabsContent>

        <TabsContent value="recordings" className="space-y-3">
          {callsWithRecordings.length === 0 ? (
            <Card className="p-12 text-center">
              <Mic className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">{t('calls.noRecordings')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('calls.trySyncRecordings')}
              </p>
            </Card>
          ) : (
            renderCallList(callsWithRecordings, recordingsPagination, "recording-", "recordings")
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
